import { useEffect, useState } from 'react'
import { Staff } from '../services/Types'
import { getStaffByProgramOffice } from '../services/EventService'
import { authenticated } from '../services/AuthService'

export function formatStaffSuggestionLabel(option: Staff | string): string {
  if (typeof option === 'string') return option
  const parts = [option.name?.trim()].filter(Boolean)
  const email = option.email?.trim()
  const programOffice = option.programOffice?.trim()
  if (email) parts.push(email)
  if (programOffice) parts.push(programOffice)
  return parts.join(' - ')
}

export function filterStaffSuggestions(options: Staff[], inputValue: string): Staff[] {
  const query = inputValue.toLowerCase()
  return options.filter(
    (opt) =>
      opt.name.toLowerCase().includes(query) ||
      opt.email.toLowerCase().includes(query) ||
      opt.programOffice.toLowerCase().includes(query)
  )
}

/**
 * Returns a deduplicated list of staff contacts previously used by events
 * belonging to the resolved Program Office. One entry per email (latest record).
 *
 * Resolution order:
 *   1. eventProgramOfficeId (passed in by the caller)
 *   2. logged-in user's programOffice.id from localStorage
 *   3. empty array (no suggestions, degrades to free-fill)
 */
export function useStaffSuggestions(eventProgramOfficeId?: number | null): Staff[] {
  const [suggestions, setSuggestions] = useState<Staff[]>([])

  useEffect(() => {
    const user = authenticated()
    const programOfficeId = eventProgramOfficeId ?? user?.programOffice?.id

    if (!programOfficeId) {
      setSuggestions([])
      return
    }

    let cancelled = false

    getStaffByProgramOffice(programOfficeId)
      .then(({ data }) => {
        if (!cancelled && data?.staffByProgramOffice?.statusCode === 200) {
          setSuggestions(data.staffByProgramOffice.data ?? [])
        }
      })
      .catch(() => {
        // Suggestions are optional - silently degrade
      })

    return () => {
      cancelled = true
    }
  }, [eventProgramOfficeId])

  return suggestions
}
